<template>
  <v-container fluid class="fill-height">
    <v-container v-if="loading">
      <v-row>
        <v-col>
          <v-progress-circular indeterminate />
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else-if="quizContent" fluid class="fill-height align-start">
      <v-overlay v-if="isSaving">
        <v-progress-circular indeterminate size="64" />
      </v-overlay>

      <v-row>
        <!--quiz left column-->
        <v-col cols="12" md="6">
          <!--quiz save, submit, approve, reject buttons done-->
          <v-row v-if="$vuetify.breakpoint.smAndDown" no-gutters justify="end">
            <v-col v-if="$store.getters.isLocaleEnabled" cols="auto">
              <v-btn
                depressed
                min-width="40"
                color="primaryDark"
                class="white--text"
                @click="selectedLocale = selectedLocale === 'en' ? 'ar' : 'en'"
              >
                {{ selectedLocale === "ar" ? "En" : "ع" }}
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <!--quiz save, submit buttons done-->
              <QuizSaveAndSubmit
                :quizContent="quizContent"
                :isSaving.sync="isSaving"
                :isEditing.sync="isEditing"
                @onCancel="resetQuizContent"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>

            <v-col cols="auto">
              <!--quiz approve, reject buttons done-->
              <QuizApproveOrReject
                :quizContent="quizContent"
                :isEditing="isEditing"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>
          </v-row>

          <!--quiz basic done-->
          <QuizBasic
            :quizContent.sync="quizContent"
            :isEditing="isEditing"
            :selectedLocale="selectedLocale"
            class="mb-6"
          />

          <!--quiz content anchor-->
          <div id="quizStart"></div>

          <QuizQuestionAnswerList
            :quizContent.sync="quizContent"
            :isEditing="isEditing"
            :selectedLocale="selectedLocale"
            class="mb-6"
          />
        </v-col>

        <!--quiz right column-->
        <v-col cols="12" md="6">
          <!--quiz save, submit, approve, reject buttons done-->
          <v-row v-if="$vuetify.breakpoint.mdAndUp" no-gutters justify="end">
            <v-col
              v-if="$store.getters.isLocaleEnabled"
              cols="auto"
              class="pb-6"
            >
              <v-btn
                depressed
                color="primaryDark"
                class="white--text"
                @click="selectedLocale = selectedLocale === 'en' ? 'ar' : 'en'"
              >
                {{ selectedLocale === "ar" ? "En" : "ع" }}
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <!--quiz save, submit buttons done-->
              <QuizSaveAndSubmit
                :quizContent="quizContent"
                :isSaving.sync="isSaving"
                :isEditing.sync="isEditing"
                @onCancel="resetQuizContent"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>

            <v-col cols="auto">
              <!--quiz approve, reject buttons done-->
              <QuizApproveOrReject
                :quizContent="quizContent"
                :isEditing="isEditing"
                @onSuccess="snackbarSuccess = true"
                @onError="snackbarError = true"
              />
            </v-col>
          </v-row>

          <QuizImage
            :quizContent.sync="quizContent"
            :isEditing="isEditing"
            class="mb-6"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else>
      <Error />
    </v-container>

    <v-snackbar v-model="snackbarSuccess" timeout="3000" color="success">
      {{
        snackbarSuccessMessage || $t("course.courseDetails.successfullySave")
      }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="primary"
          text
          v-bind="attrs"
          @click="snackbarSuccess = false"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="snackbarError" timeout="3000" color="error">
      {{ snackbarErrorMessage || $t("somethingWentWrong") }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="primary"
          text
          v-bind="attrs"
          @click="snackbarError = false"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import QuizBasic from "@/app/quiz-module/components/details/QuizBasic";
import QuizSaveAndSubmit from "@/app/quiz-module/components/details/QuizSaveAndSubmit";
import QuizApproveOrReject from "@/app/quiz-module/components/details/QuizApproveOrReject";
import QuizImage from "../components/details/QuizImage";
import QuizQuestionAnswerList from "../components/details/QuizQuestionAnswerList";
import Error from "../../core-module/components/Error";

export default {
  name: "QuizDetailsPage",
  components: {
    QuizBasic,
    QuizSaveAndSubmit,
    QuizApproveOrReject,
    QuizImage,
    QuizQuestionAnswerList,
    Error,
  },
  data() {
    return {
      snackbarSuccess: false,
      snackbarError: false,
      snackbarSuccessMessage: null,
      snackbarErrorMessage: null,
      loading: true,
      quizContentOg: null,
      quizContent: null,
      isSaving: false,
      isEditing: false,
      selectedLocale: "en",
    };
  },
  watch: {
    "$route.params.quizId": {
      handler() {
        this.fetchQuizContent();
      },
      immediate: true,
    },
  },
  methods: {
    fetchQuizContent() {
      this.loading = true;
      this.quizContentOg = null;
      this.quizContent = null;

      if (this.$route.params.quizId) {
        this.$apollo.addSmartQuery("getQuizContent", {
          query: require("../api/query/getQuizContent.gql"),
          variables: { id: this.$route.params.quizId },
          result({ data, loading }) {
            if (data && data.getQuizContent) {
              this.quizContentOg = data.getQuizContent;
              this.quizContent = this.deepCloneObject(this.quizContentOg);
              this.quizContent = this.validateQuizContent(this.quizContent);
            }

            this.loading = loading;
          },
        });
      } else {
        this.quizContentOg = {
          isActive: true,
          hasInvalidTitle: true,
        };

        this.quizContent = this.deepCloneObject(this.quizContentOg);
        this.isEditing = true;
        this.loading = false;
      }
    },
    resetQuizContent() {
      this.quizContent = this.deepCloneObject(this.quizContentOg);
      this.quizContent = this.validateQuizContent(this.quizContent);
    },
  },
};
</script>

<style lang="scss" scoped />
